import { createFileRoute, Link } from '@tanstack/react-router'
import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'
import { Loader2, Plus } from 'lucide-react'
import {
  adminListPlanEligibility,
  adminSetPlanEligibility,
} from '@/gen/cortado/v1/admin-AdminService_connectquery'
import type { PlanEligibility } from '@/gen/cortado/v1/admin_pb'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { OrganizationSelect } from '@/components/billing/organization-select'
import {
  formatDate,
  planEligibilityAddHint,
  planEligibilityListNoun,
  type BadgeVariant,
} from '@/lib/billing'

export const Route = createFileRoute('/_admin/billing/eligibility')({
  component: EligibilityPage,
})

type ListKind = 'founder' | 'ccc'

const STATUS_OPTIONS = ['active', 'inactive', 'unverified'] as const

const STATUS_BADGE: Record<string, BadgeVariant> = {
  active: 'success',
  inactive: 'secondary',
  unverified: 'warning',
}

const STATUS_SOURCE_LABEL: Record<string, string> = {
  frozen_list: 'Frozen list (launch seed)',
  admin: 'Set by admin',
  ccc_sync: 'Recorded read from CCC data',
  system: 'System (rate forfeiture)',
}

const eligibilityListKey = createConnectQueryKey({
  schema: adminListPlanEligibility,
  cardinality: undefined,
})

function EligibilityPage() {
  const [tab, setTab] = useState<ListKind>('founder')

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Founder &amp; CCC eligibility</h1>
        <p className="text-muted-foreground text-sm">
          Plan eligibility lists that drive R-71's assigned-plan order (Founder overrides everything,
          then active CCC membership, then Standard).
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Button variant={tab === 'founder' ? 'default' : 'outline'} size="sm" onClick={() => setTab('founder')}>
          Founder
        </Button>
        <Button variant={tab === 'ccc' ? 'default' : 'outline'} size="sm" onClick={() => setTab('ccc')}>
          Coffee Cart Collective
        </Button>
      </div>

      {tab === 'founder' ? <EligibilityList listKind="founder" /> : <EligibilityList listKind="ccc" />}
    </div>
  )
}

function EligibilityList({ listKind }: { listKind: ListKind }) {
  const { data, isLoading, isError, error } = useQuery(adminListPlanEligibility, { listKind })
  const rows = data?.planEligibility ?? []
  const [editRow, setEditRow] = useState<PlanEligibility | null>(null)
  const [addOpen, setAddOpen] = useState(false)

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">{planEligibilityAddHint(listKind)}</p>
        <Button size="sm" className="w-full sm:w-auto" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4" />
          Add {planEligibilityListNoun(listKind)}
        </Button>
      </div>

      {isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Unable to load eligibility list'}
        </div>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Status read at</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : !rows.length ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No {listKind === 'founder' ? 'Founder' : 'CCC'} rows found
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">
                      {row.organizationId ? (
                        <Link
                          to="/billing/$organizationId"
                          params={{ organizationId: row.organizationId }}
                          className="hover:underline"
                        >
                          {row.accountName}
                        </Link>
                      ) : (
                        row.accountName
                      )}
                      {!row.organizationId && (
                        <div className="text-muted-foreground text-xs">Not yet matched to an organization</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_BADGE[row.status] ?? 'secondary'}>{row.status}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(row.statusReadAt)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {STATUS_SOURCE_LABEL[row.statusSource] ?? row.statusSource}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-xs truncate" title={row.notes}>
                      {row.notes || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!row.organizationId}
                        onClick={() => setEditRow(row)}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {editRow && (
        <PlanEligibilityDialog
          listKind={listKind}
          mode="edit"
          organizationId={editRow.organizationId ?? ''}
          organizationName={editRow.accountName}
          initialStatus={editRow.status}
          initialNotes={editRow.notes ?? ''}
          open
          onOpenChange={(open) => !open && setEditRow(null)}
        />
      )}

      {addOpen && (
        <PlanEligibilityDialog
          listKind={listKind}
          mode="add"
          open
          onOpenChange={setAddOpen}
        />
      )}
    </div>
  )
}

function PlanEligibilityDialog({
  listKind,
  mode,
  organizationId: initialOrgId,
  organizationName: initialOrgName,
  initialStatus,
  initialNotes,
  open,
  onOpenChange,
}: {
  listKind: ListKind
  mode: 'add' | 'edit'
  organizationId?: string
  organizationName?: string
  initialStatus?: string
  initialNotes?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const [organizationId, setOrganizationId] = useState(initialOrgId ?? '')
  const [organizationName, setOrganizationName] = useState(initialOrgName ?? '')
  const [status, setStatus] = useState(initialStatus ?? 'active')
  const [notes, setNotes] = useState(initialNotes ?? '')

  const mutation = useMutation(adminSetPlanEligibility, {
    onSuccess: () => {
      toast.success(mode === 'add' ? `${planEligibilityListNoun(listKind)} added` : 'Eligibility updated')
      queryClient.invalidateQueries({ queryKey: eligibilityListKey })
      onOpenChange(false)
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to save eligibility')
    },
  })

  const canSubmit = Boolean(organizationId) && !mutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'add'
              ? `Add ${planEligibilityListNoun(listKind)}`
              : `Edit ${listKind === 'founder' ? 'Founder' : 'CCC'} status`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-2">
            <Label>Organization</Label>
            {mode === 'add' ? (
              <OrganizationSelect
                value={organizationId}
                onValueChange={(id, name) => {
                  setOrganizationId(id)
                  setOrganizationName(name)
                }}
              />
            ) : (
              <p className="text-sm font-medium">{organizationName}</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {listKind === 'ccc' && (
              <p className="text-muted-foreground text-xs">
                Corrections to CCC membership are made in Coffee Cart Collective and flow through on
                the next sync — this is a recorded status, not an editable source of truth.
              </p>
            )}
          </div>
          <div className="grid gap-2">
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                mutation.mutate({
                  organizationId,
                  listKind,
                  status,
                  notes: notes.trim() || undefined,
                })
              }
              disabled={!canSubmit}
            >
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
